import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaUser,
  FaLock,
  FaSignInAlt,
  FaUserPlus,
  FaGoogle,
  FaVk,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../../components/Logo";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [emailVerificationRequired, setEmailVerificationRequired] =
    useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");

  // Références pour les champs du formulaire pour l'accessibilité
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    setMounted(true);

    // Mettre le focus sur le premier champ pour l'accessibilité
    if (emailRef.current) {
      emailRef.current.focus();
    }

    if (location.state?.email) {
      setEmail(location.state.email);
    }

    if (location.state?.verificationNeeded) {
      toast.info(
        "Un email de vérification a été envoyé à votre adresse. Veuillez vérifier votre boîte de réception et vos spams.",
        { autoClose: 8000 },
      );
    }
  }, [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (!password) {
      newErrors.password = "Le mot de passe est requis";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Mettre le focus sur le premier champ avec erreur
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField === "email" && emailRef.current)
        emailRef.current.focus();
      if (firstErrorField === "password" && passwordRef.current)
        passwordRef.current.focus();

      return;
    }

    try {
      setLoading(true);
      setEmailVerificationRequired(false);

      const user = await login(email, password);

      if (user) {
        toast.success("Connexion réussie !");
        navigate("/dashboard");
      }
    } catch (error) {
      // Gestion des erreurs spécifiques
      if (error.response?.data?.error === "account_not_verified") {
        setEmailVerificationRequired(true);
        setVerificationEmail(email);
      } else if (error.response?.data?.error === "account_locked") {
        const lockUntil = new Date(error.response.data.lockUntil);
        const now = new Date();
        const minutesLeft = Math.ceil((lockUntil - now) / 1000 / 60);

        toast.error(
          `Compte bloqué. Veuillez réessayer dans ${minutesLeft} minute(s) ou réinitialiser votre mot de passe.`,
        );
      } else if (error.response?.data?.error === "wrong_password") {
        const attempts = error.response?.data?.remainingAttempts || 0;
        setErrors({
          ...errors,
          password: `Mot de passe incorrect. ${attempts} tentative(s) restante(s) avant blocage du compte.`,
        });
      } else {
        const serverMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message;
        toast.error(`Erreur: ${serverMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      navigate("/resend-verification", { state: { email: verificationEmail } });
    } catch (error) {
      toast.error("Erreur lors de la redirection vers la page de renvoi");
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const floatingVariants = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut",
      },
    },
  };

  const handleSocialLogin = async (provider) => {
    try {
      setLoading(true);
      console.log(`Tentative de connexion avec ${provider}`);
      // À implémenter avec l'API appropriée quand disponible
      toast.info(`Connexion avec ${provider} en cours de développement`);
    } catch (error) {
      console.error(`Erreur de connexion avec ${provider}:`, error);
      toast.error(`Erreur lors de la connexion avec ${provider}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Cercles décoratifs flottants */}
      <motion.div
        className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-green-300/20 to-blue-300/20 blur-3xl -top-20 -left-20"
        animate={{
          x: [0, 20, 0],
          y: [0, 30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{ zIndex: 1 }}
      />

      <motion.div
        className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-blue-300/20 to-purple-300/20 blur-3xl -bottom-40 -right-20"
        animate={{
          x: [0, -30, 0],
          y: [0, -20, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{ zIndex: 1 }}
      />

      {/* Particules animées */}
      <motion.div className="absolute inset-0 z-0 opacity-30 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -Math.random() * 100 - 50],
              opacity: [0, 1, 0],
              scale: [0, Math.random() + 0.5, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 3,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </motion.div>

      <motion.div
        className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden w-full max-w-md relative z-10 border border-white/40"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ zIndex: 50 }}
      >
        <div className="p-8">
          <motion.div
            className="flex justify-center mb-6"
            variants={floatingVariants}
            animate="animate"
          >
            <Logo size="large" animated={mounted} />
          </motion.div>

          <motion.h1
            className="text-2xl font-bold text-center text-gray-800 mb-6"
            variants={itemVariants}
          >
            Connexion
          </motion.h1>

          {/* Afficher l'alerte si l'email n'est pas vérifié */}
          {emailVerificationRequired && (
            <motion.div
              className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded"
              variants={itemVariants}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Veuillez vérifier votre adresse email avant de vous
                    connecter.
                  </p>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-sm text-yellow-700 font-medium bg-yellow-100 hover:bg-yellow-200 px-3 py-1 rounded transition-colors"
                      onClick={handleResendVerification}
                    >
                      Renvoyer l'email de vérification
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <motion.form
            className="space-y-4"
            variants={itemVariants}
            onSubmit={handleSubmit}
            noValidate
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  ref={emailRef}
                  type="email"
                  autoComplete="email"
                  required
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"} rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: null });
                  }}
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600" id="email-error">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Mot de passe
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-green-600 hover:text-green-500"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className={`block w-full pl-10 pr-10 py-2 border ${errors.password ? "border-red-500" : "border-gray-300"} rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password)
                      setErrors({ ...errors, password: null });
                  }}
                  aria-invalid={errors.password ? "true" : "false"}
                  aria-describedby={
                    errors.password ? "password-error" : undefined
                  }
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600" id="password-error">
                  {errors.password}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  type="checkbox"
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label
                  htmlFor="rememberMe"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Se souvenir de moi
                </label>
              </div>
            </div>

            <motion.button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Connexion en cours...
                </span>
              ) : (
                <span className="flex items-center">
                  <FaSignInAlt className="mr-2" />
                  Se connecter
                </span>
              )}
            </motion.button>
          </motion.form>

          <motion.div className="mt-6 text-center" variants={itemVariants}>
            <p className="text-sm text-gray-600">
              Pas encore de compte ?{" "}
              <Link
                to="/register"
                className="font-medium text-green-600 hover:text-green-500 flex items-center justify-center"
              >
                <FaUserPlus className="mr-1" />
                S'inscrire
              </Link>
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
